/**
 * Portal certificate hooks -- recipient-facing course certificates.
 *
 * BE endpoints (see src/modules/portal/portal-certificates.controller.ts):
 *   GET  /v1/portal/certificates                 -- list all certs for recipient
 *   GET  /v1/portal/certificates/:id             -- single cert by completion id
 *   GET  /v1/portal/certificates/:id/download    -- PDF blob
 *   GET  /v1/portal/certificates/verify/:code    -- public verify by short code
 *
 * The list / detail / download endpoints require the portal JWT
 * (`hf-portal:token`). Verify is public -- no auth required.
 *
 * Pattern-matches `use-portal-training.ts`.
 */
import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_CONFIG } from '@/lib/api/config';
import { portalAuthApi } from '@/lib/api/portal-auth';
import { filenameFromContentDisposition } from '@/lib/http/content-disposition';

// ============================================================================
// Portal Fetch Wrappers
// ============================================================================

const PORTAL_PREFIX = '/portal/certificates';

async function portalFetch<T>(
  path: string,
  opts?: RequestInit,
): Promise<T> {
  const url = `${API_CONFIG.baseUrl}${API_CONFIG.versionPrefix}${path}`;
  const buildInit = (tokenOverride?: string | null): RequestInit => {
    const t = tokenOverride ?? portalAuthApi.getToken();
    return {
      ...opts,
      headers: {
        'Content-Type': 'application/json',
        ...(t ? { Authorization: `Bearer ${t}` } : {}),
        ...(opts?.headers as Record<string, string> | undefined),
      },
    };
  };

  // 2026-05-22 — refresh-on-401 retry (mirrors portalAuthApi.authedFetch).
  // Same fix as use-portal-training.ts. Without this, an expired access
  // token surfaces as "Course Not Available" even when a valid refresh
  // token is sitting in localStorage.
  let res = await fetch(url, buildInit());
  if (res.status === 401) {
    const refreshed = await portalAuthApi.refresh();
    if (refreshed) {
      res = await fetch(url, buildInit(refreshed));
    }
  }

  if (!res.ok) {
    const body = await res.text().catch(() => 'Unknown error');
    throw new Error(`${res.status}: ${body}`);
  }

  return res.json() as Promise<T>;
}

/**
 * Fetches a binary blob (PDF). Uses portal JWT. Used by the download mutation.
 */
async function portalFetchBlob(path: string): Promise<{
  blob: Blob;
  filename: string | null;
}> {
  const url = `${API_CONFIG.baseUrl}${API_CONFIG.versionPrefix}${path}`;
  const buildInit = (tokenOverride?: string | null): RequestInit => {
    const t = tokenOverride ?? portalAuthApi.getToken();
    return {
      headers: { ...(t ? { Authorization: `Bearer ${t}` } : {}) },
    };
  };

  // 2026-05-22 — refresh-on-401 retry. Same pattern as portalFetch above.
  let res = await fetch(url, buildInit());
  if (res.status === 401) {
    const refreshed = await portalAuthApi.refresh();
    if (refreshed) {
      res = await fetch(url, buildInit(refreshed));
    }
  }

  if (!res.ok) {
    const body = await res.text().catch(() => 'Unknown error');
    throw new Error(`${res.status}: ${body}`);
  }

  // The API names the file with contentDisposition(): an ASCII fallback in
  // filename="…" and the real name in filename*=UTF-8''…, which is read first
  // (the fallback saved "مقدمة في الأمن" as "certificate-_-_-_.pdf").
  const filename = filenameFromContentDisposition(res.headers.get('Content-Disposition'));

  const blob = await res.blob();
  return { blob, filename };
}

// ============================================================================
// Types (mirror BE PortalCertificateDto + VerifyCertificateDto)
// ============================================================================

export interface PortalCertificate {
  id: string;
  enrollmentId: string;
  courseId: string;
  courseName: string;
  recipientName: string;
  score: number | null;
  completionDate: string;
  certificateIssuedAt: string;
  certificateCode: string;
  downloadUrl: string | null;
  verifyUrl: string;
  // Fields added in BE Phase 4 / wave 2 — may be null if not yet emitted by BE
  courseCode: string | null;
  expiryDate: string | null;
  templateName: string | null;
  jwsToken: string | null;
  qrCodeDataUrl: string | null;
}

export interface VerifyCertificateResult {
  valid: boolean;
  courseName?: string;
  recipientName?: string;
  completionDate?: string;
  score?: number | null;
  certificateCode?: string;
}

// ============================================================================
// Query Key Factory
// ============================================================================

export const portalCertificateKeys = {
  all: ['portal', 'certificates'] as const,
  list: () => [...portalCertificateKeys.all, 'list'] as const,
  detail: (id: string) =>
    [...portalCertificateKeys.all, 'detail', id] as const,
  verify: (code: string) =>
    [...portalCertificateKeys.all, 'verify', code] as const,
};

// ============================================================================
// Queries
// ============================================================================

/**
 * List all certificates for the authenticated recipient.
 * BE returns `{ data: PortalCertificate[] }` envelope.
 */
export function usePortalCertificates() {
  return useQuery({
    queryKey: portalCertificateKeys.list(),
    queryFn: () =>
      portalFetch<{ data: PortalCertificate[] }>(PORTAL_PREFIX),
    placeholderData: keepPreviousData,
    select: (res) => res.data ?? [],
  });
}

/**
 * The certificate's PDF for the inline preview, fetched with the portal JWT
 * exactly as Download fetches it. A frame cannot send the token, and the
 * list's `downloadUrl` is a relative API path the portal host does not serve,
 * so framing it showed nothing on any environment (2026-09-23). Fetched only
 * once the learner opens the preview (`enabled`).
 */
export function usePortalCertificatePdf(id: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: [...portalCertificateKeys.detail(id ?? ''), 'pdf'],
    queryFn: async () => (await portalFetchBlob(`${PORTAL_PREFIX}/${id}/download`)).blob,
    enabled: Boolean(id) && enabled,
    staleTime: Infinity,
  });
}

/**
 * Fetch a single certificate by completion id.
 */
export function usePortalCertificate(id: string) {
  return useQuery({
    queryKey: portalCertificateKeys.detail(id),
    queryFn: () =>
      portalFetch<PortalCertificate>(`${PORTAL_PREFIX}/${id}`),
    enabled: !!id,
  });
}

/**
 * Public certificate verification by short code.
 * No auth required -- anyone with a code can verify.
 */
export function useVerifyCertificate(code: string) {
  return useQuery({
    queryKey: portalCertificateKeys.verify(code),
    queryFn: () =>
      portalFetch<VerifyCertificateResult>(
        `${PORTAL_PREFIX}/verify/${code}`,
      ),
    enabled: !!code,
  });
}

// ============================================================================
// Mutations
// ============================================================================

/**
 * Download certificate PDF + trigger browser save-as.
 *
 * Usage:
 *   const download = useDownloadPortalCertificate();
 *   download.mutate({ id: cert.id, courseName: cert.courseName });
 */
export function useDownloadPortalCertificate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      courseName,
    }: {
      id: string;
      courseName?: string;
    }) => {
      const { blob, filename } = await portalFetchBlob(
        `${PORTAL_PREFIX}/${id}/download`,
      );

      // Only when the server sent no name. Letters and digits of any script
      // survive, as in the API's own name (portal-certificates.controller.ts);
      // a ZWNJ/ZWJ is dropped, since browsers turn it into "_" in a file name.
      const safeCourse =
        (courseName ?? '')
          .replace(/[\u200C\u200D]/g, '')
          .replace(/[^\p{L}\p{M}\p{N}]+/gu, '-')
          .replace(/^-+|-+$/g, '') || 'course';
      const fallback = `certificate-${safeCourse}.pdf`;

      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename ?? fallback;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);

      return { ok: true as const };
    },
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({ queryKey: portalCertificateKeys.detail(vars.id) });
    },
  });
}
